/* eslint-disable max-lines */
/**
 * SourcesButton Component
 * EVI-405: Chat Input with Source Control (Pop-over, Badges & State)
 *
 * A combined component that renders:
 * 1. A "Sources" button that opens the SourceControlPopover (desktop) or SourceControlMobileDrawer (mobile)
 * 2. Source badges showing the current selection
 * 3. An onboarding tooltip for first-time users
 */

import { openSourceControlPopoverAtom, sourceControlToggleAtom } from '@/atoms/sourceControlAtoms';;
import {
  clinicianUpgradeModalOpenAtom,
  practiceUpgradeModalOpenAtom,
} from '@/atoms/paywallAtoms';
import { useEvidenceCapabilities } from '@/atoms/evidenceCapabilitiesAtoms';
import { Button, type ButtonVariant } from '@heidi/design-system/button';
import { TypographyCaption } from '@heidi/design-system/typography';
import { useSourceLists } from '@heidi/evidence/data';
import { StackedSourceAvatars } from '@/components/ui/floating-ask-heidi/evidence-prompts-bar';
import { useIsEvidenceSummarySuggestionEnabled } from '@/app-shell/flags/evidence-flags';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import useReverseTrial from '@/hooks/useReverseTrial';
import { track } from '@/lib/analytics/track';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { isEvidenceApiAvailable } from '@/utils/backendSurfaces';
import type { PersonalisedSourcesControlProps } from '@/models/analytics/features/evidence-shortcuts';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { defineMessages, useIntl } from 'react-intl';
import { useAvailableIntegrations } from '@/components/chat/hooks/useAvailableIntegrations';
import { TextSearch } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { SourceControlMobileDrawer } from './SourceControlMobileDrawer';
import { SourceControlOnboardingTooltip } from './SourceControlOnboardingTooltip';
import {
  PersonalisedSourceAvatars,
  SourceControlPopoverV2,
  SourceModeIcons,
} from './source-control-v2';
import { formatPersonalisedSourceTooltip } from './source-control-v2/personalised-source-avatars.utils';
import { usePersonalisedSourceSummary } from './source-control-v2/use-personalised-source-summary';
import { SourcesSpotlightTooltip } from './SourcesSpotlightTooltip';
import { useDocumentCreationSuggestionBanner } from '@/hooks/useDocumentCreationSuggestionBanner';
import { useCurrentSessionId } from '@heidi/runtime/shell';

interface SourcesButtonProps extends PersonalisedSourcesControlProps {
  /** Whether to use mobile styling */
  isMobile?: boolean;
  /** Disable opening source control when chat input is disabled */
  disabled?: boolean;
  /** Whether to show the onboarding tooltip (only show on /chat page, not in-session) */
  enableOnboardingTooltip?: boolean;
  /** Whether to show compact (icon-only) badges. Used for in-session view to reduce clutter. */
  compactBadges?: boolean;
  /**
   * Which side to show the popover on. Defaults to 'top'.
   * Use 'bottom' on the Evidence homepage where input is centered.
   */
  popoverSide?: 'top' | 'bottom';
  /**
   * Content to render between the source button and the source badges.
   * Used to insert other controls (e.g., ProtocolsButton) in a stable position.
   */
  slotAfterButton?: ReactNode;
  /** Whether to show the evidence onboarding sources spotlight (step 2/3). In-session sidebar only. */
  enableSourcesSpotlight?: boolean;
  /** Force a specific button variant, bypassing the default pro-brand styling logic. */
  buttonVariant?: ButtonVariant;
  /** Horizontal alignment of the popover relative to the trigger. Defaults to 'start' via the underlying popover. */
  popoverAlign?: 'start' | 'end';
  /** Hide source badges entirely (e.g., in the floating input where space is limited) */
  hideBadges?: boolean;
  // Override the auto active-state styling: `true` forces purple,
  // `false` forces ghost, `undefined` follows the default rules.
  forceActiveStyling?: boolean;
  /** Personalised stack presentation; `work` matches the Work home composer's Sources face. */
  sourceAvatarVariant?: 'evidence' | 'work';
  // TODO: Add freeSearchesRemaining prop when backend implements usage tracking
  // freeSearchesRemaining?: number;
}

const messages = defineMessages({
  localLibraryScope: {
    defaultMessage: 'Searching the local library',
    id: '6VD8Tr8DaF',
    description:
      'Quiet label in the Evidence chat composer on an on-premises appliance that cannot search the web, in place of the source selector. Tells the clinician answers come only from the library installed on the appliance',
  },
});

/**
 * The source control is built over the `/evidence` aggregate: "My library",
 * the partner sources and the upgrade banner all read or write it. An on-prem
 * build does not mount it, so the control would offer a "Create a collection"
 * entry that 404s and an upgrade banner. The whole control is withheld
 * instead, here rather than at SourcesButton's four mount points so the next
 * composer that mounts it is covered too.
 *
 * Where it is withheld, what the appliance searches is read at runtime (F9,
 * MOD-1726): with no web search the one scope is the local library, so a
 * static label says so. An appliance that reports web search has more than
 * one scope and no selector for them yet, so it shows nothing, as before.
 *
 * `SourceControlPopoverV2` has two other mounts that do not go through this
 * component, both in the Work composer: `EvidenceComposerSourcesButton` and
 * the add-menu's `WorkSourcesPopover`. Each carries the same surface term; a
 * new direct mount of the popover needs it too.
 */
export const SourcesButton = (props: SourcesButtonProps) => {
  const { webSearch } = useEvidenceCapabilities();
  if (isEvidenceApiAvailable) {
    return <SourceControlButton {...props} />;
  }
  const showsScopeLabel = !webSearch && !props.hideBadges;
  if (!showsScopeLabel && !props.slotAfterButton) {
    return null;
  }
  return (
    <div className="flex items-center gap-1">
      {showsScopeLabel && <LocalLibraryScopeLabel />}
      {props.slotAfterButton}
    </div>
  );
};

const LocalLibraryScopeLabel = () => {
  const intl = useIntl();
  return (
    <TypographyCaption
      className="text-text-tertiary"
      data-testid="evidence-source-scope-label"
    >
      {intl.formatMessage(messages.localLibraryScope)}
    </TypographyCaption>
  );
};

const SourceControlButton = ({
  isMobile = false,
  disabled = false,
  enableOnboardingTooltip = false,
  compactBadges = false,
  popoverSide = 'top',
  slotAfterButton,
  enableSourcesSpotlight = false,
  buttonVariant,
  popoverAlign,
  hideBadges = false,
  forceActiveStyling,
  sourceAvatarVariant = 'evidence',
  personalisedSourceLogos = false,
  analyticsSurface = 'chat_input',
}: SourcesButtonProps) => {
  const { t } = useTranslations();
  const intl = useIntl();
  const { hasEvidencePro } = useEvidenceBillingInfo();
  // ponytail: chat quarantine is shrink-only — cannot add SourcesButton→features/query
  // edges. Paywall atoms + useReverseTrial stay outside productDomainGlobs.
  const setClinicianUpgradeModalOpen = useSetAtom(clinicianUpgradeModalOpenAtom);
  const setPracticeUpgradeModalOpen = useSetAtom(practiceUpgradeModalOpenAtom);
  const { isClinicianPracticeTrialUser, isScribeTeamUser } = useReverseTrial();
  const { partners: availablePartners } = useAvailableIntegrations();

  const sessionId = useCurrentSessionId();
  const { isShowing: showingDocumentCreationSuggestions } = useDocumentCreationSuggestionBanner(sessionId ?? undefined, { trackImpressions: false });

  // GRO-2830: Resolve active web collection domains to favicon sources for the consolidated button.
  // Gated behind evidence summary suggestion experiment flag.
  const isEvidenceSummarySuggestionEnabled = useIsEvidenceSummarySuggestionEnabled();
  const { data: sourceLists } = useSourceLists();
  // Determine whether any source mode is actively toggled on.
  // The pro button styling should only apply when the user has Pro AND at least one source is active.
  const toggleState = useAtomValue(sourceControlToggleAtom);
  // Personalised path: scope toggles are filters, not "active sources".
  // Legacy path keeps webSearch / guidelines / evidence in the active check.
  const hasAnySourceActive =
    (!personalisedSourceLogos &&
      (toggleState.webSearch ||
        toggleState.localGuidelines ||
        toggleState.primaryEvidence)) ||
    toggleState.myLibrary ||
    toggleState.webCollections ||
    availablePartners.some(
      (p) => toggleState.integrationPartners[p.id] === true
    );

  const { summary: personalisedSummary, selectionTotal } =
    usePersonalisedSourceSummary(personalisedSourceLogos);
  const isPersonalisedDefault = personalisedSummary.isDefault;

  const activeWebCollectionFavicons = useMemo(() => {
    if (!isEvidenceSummarySuggestionEnabled || showingDocumentCreationSuggestions) return [];
    if (!toggleState.webCollections || !sourceLists) return [];
    const selectedIds = new Set(toggleState.selectedWebCollectionIds);
    return sourceLists
      .filter((sl) => selectedIds.has(sl.id))
      .flatMap((sl) => sl.domains)
      .map((domain) => ({ domain }));
  }, [isEvidenceSummarySuggestionEnabled, showingDocumentCreationSuggestions, toggleState.webCollections, toggleState.selectedWebCollectionIds, sourceLists]);

  // Local popover open state - each SourcesButton instance manages its own state
  // to prevent multiple instances from opening simultaneously when they share the
  // same global atom (e.g., sidebar "Ask Heidi" panel + main chat page).
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Listen to the global atom for programmatic opening (e.g., trial banner "Try now").
  // Treats the atom as a one-shot trigger: when it becomes true, open this popover
  // and immediately reset the atom so other instances don't also open.
  const [openFromAtom, setOpenFromAtom] = useAtom(openSourceControlPopoverAtom);
  useEffect(() => {
    if (openFromAtom) {
      setIsPopoverOpen(true);
      setOpenFromAtom(false);
    }
  }, [openFromAtom, setOpenFromAtom]);

  const [isTooltipActive, setIsTooltipActive] = useState(false);

  // When user clicks "Try source control" in the onboarding tooltip,
  // open the popover directly via local state
  // This is called after the tooltip dismisses (with delay) to prevent overlap
  const handleTrySourceControl = useCallback(() => {
    // Ensure tooltip is fully dismissed before opening popover
    setIsTooltipActive(false);
    setIsPopoverOpen(true);
  }, []);

  // When user clicks "Upgrade to Pro" in the post-trial tooltip,
  // open Clinician/Practice upgrade (Evidence Pro trial-ended modal retired).
  // Mirrors isPracticeUpgradeTarget half of resolveEvidenceUpgradeModalTarget
  // without importing growth/query (chat outbound ratchet is shrink-only).
  const handleUpgrade = useCallback(() => {
    setIsTooltipActive(false);
    if (isScribeTeamUser || isClinicianPracticeTrialUser) {
      setPracticeUpgradeModalOpen(true);
      return;
    }
    setClinicianUpgradeModalOpen(true);
  }, [
    isClinicianPracticeTrialUser,
    isScribeTeamUser,
    setClinicianUpgradeModalOpen,
    setPracticeUpgradeModalOpen,
  ]);

  // Block popover from opening while tooltip is active
  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      if (disabled && open) return;
      // Don't allow popover to open if tooltip is showing
      // User must click "Try source control" button instead
      if (open && isTooltipActive) {
        return;
      }
      setIsPopoverOpen(open);

      // If popover is opening, ensure tooltip is dismissed
      if (open) {
        setIsTooltipActive(false);

        // Personalised Sources button open — record surface + selection shape.
        if (personalisedSourceLogos) {
          track({
            event_name: TrackingEventName.EvidenceSourcesOpened,
            props: {
              surface: analyticsSurface,
              is_default: isPersonalisedDefault,
              selection_total: selectionTotal,
            },
            options: { amplitude: false },
          });
        }
      }
    },
    [
      disabled,
      isTooltipActive,
      personalisedSourceLogos,
      analyticsSurface,
      isPersonalisedDefault,
      selectionTotal,
    ]
  );

  const personalisedSelectedButtonClassName =
    'bg-entity-neutral-muted text-entity-neutral-foreground hover:bg-entity-neutral active:bg-entity-neutral';
  const personalisedNeutralButtonClassName =
    'border border-outline bg-transparent text-text-primary hover:bg-surface-hover active:bg-surface-pressed';

  const sourceButtonIcon = activeWebCollectionFavicons.length > 0 ? (
    <StackedSourceAvatars sources={activeWebCollectionFavicons} size="sm" maxVisible={2} overflowVariant="pro" />
  ) : (
    <TextSearch />
  );

  // Personalised path ignores forceActiveStyling; sand fill = non-default corpus.
  const isPersonalisedActive = personalisedSourceLogos
    ? !isPersonalisedDefault
    : (forceActiveStyling ?? (hasEvidencePro && hasAnySourceActive));

  const isClinicianActiveStyle = !personalisedSourceLogos && isPersonalisedActive;

  const trailingIconElement = personalisedSourceLogos ? (
    // The logo stack is this button's whole face, and it resolves from queries
    // that have not landed on first paint — without a fallback the trigger
    // draws as an empty box until they do.
    <PersonalisedSourceAvatars
      variant={sourceAvatarVariant}
      fallback={<TextSearch />}
    />
  ) : (
    <SourceModeIcons />
  );

  const shouldShowTrailingIcon = personalisedSourceLogos || hasEvidencePro;

  // Mobile: Use bottom sheet drawer
  if (isMobile) {
    return (
      <div className="flex items-center gap-1">
        <SourceControlMobileDrawer
          open={isPopoverOpen}
          onOpenChange={handlePopoverOpenChange}
        >
          <Button
            type="button"
            variant={
              isClinicianActiveStyle
                ? 'outline'
                : hasEvidencePro && hasAnySourceActive
                  ? 'secondary'
                  : 'ghost'
            }
            size="lg"
            data-testid="sources-button"
            disabled={disabled}
            className={cn(
              personalisedSourceLogos
                ? isPersonalisedActive
                  ? personalisedSelectedButtonClassName
                  : personalisedNeutralButtonClassName
                : undefined
            )}
          >
            {!isClinicianActiveStyle && sourceButtonIcon}
            {shouldShowTrailingIcon &&
              (isClinicianActiveStyle ||
                activeWebCollectionFavicons.length === 0) &&
              trailingIconElement}
          </Button>
        </SourceControlMobileDrawer>
        {slotAfterButton}
      </div>
    );
  }

  const personalisedTooltip = formatPersonalisedSourceTooltip(
    intl,
    personalisedSummary
  );

  const buttonWithPopover = (
    <SourceControlPopoverV2
      open={isPopoverOpen}
      onOpenChange={handlePopoverOpenChange}
      side={popoverSide}
      align={popoverAlign}
      tooltipContent={
        enableOnboardingTooltip
          ? undefined
          : personalisedSourceLogos
            ? personalisedTooltip
            : t('sources.chooseSourcesTooltip' as TranslationKey)
      }
    >
      <Button
        type="button"
        variant={
          buttonVariant ??
          (isClinicianActiveStyle
            ? 'outline'
            : isPersonalisedActive
              ? 'secondary'
              : 'ghost')
        }
        size={personalisedSourceLogos ? 'md' : 'sm'}
        data-testid="sources-button"
        disabled={disabled}
        aria-label={
          personalisedSourceLogos ? t('sources.sourcesButton') : undefined
        }
        className={cn(
          !buttonVariant && isPersonalisedActive && personalisedSourceLogos
            ? personalisedSelectedButtonClassName
            : !buttonVariant &&
                personalisedSourceLogos &&
                isPersonalisedDefault
              ? personalisedNeutralButtonClassName
              : undefined
        )}
      >
        {!personalisedSourceLogos && !isClinicianActiveStyle && sourceButtonIcon}
        {!personalisedSourceLogos && t('sources.sourcesButton')}
        {/* The personalised stack is the trigger's whole face, so a forced variant keeps it. */}
        {(personalisedSourceLogos ||
          (!buttonVariant &&
            shouldShowTrailingIcon &&
            (isClinicianActiveStyle || activeWebCollectionFavicons.length === 0))) &&
          trailingIconElement}
      </Button>
    </SourceControlPopoverV2>
  );

  const sourcesButtonContent = enableOnboardingTooltip ? (
    <SourceControlOnboardingTooltip
      onTrySourceControl={handleTrySourceControl}
      onUpgrade={handleUpgrade}
      onTooltipOpenChange={setIsTooltipActive}
      shouldShow={!isPopoverOpen}
    >
      {buttonWithPopover}
    </SourceControlOnboardingTooltip>
  ) : (
    buttonWithPopover
  );

  return (
    <div className="flex items-center gap-1">
      {enableSourcesSpotlight ? (
        <SourcesSpotlightTooltip>
          <span className="inline-flex">{sourcesButtonContent}</span>
        </SourcesSpotlightTooltip>
      ) : (
        sourcesButtonContent
      )}
      {slotAfterButton}
    </div>
  );
};
